import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { usePractitionerCredits } from '@/hooks/usePractitionerCredits';
import { CreditCard, Gift, Coins, AlertTriangle, TrendingDown } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface UserCreditBalanceProps {
  compact?: boolean;
  showTransactions?: boolean;
}

export const UserCreditBalance: React.FC<UserCreditBalanceProps> = ({
  compact = false,
  showTransactions = false
}) => {
  const { credits, transactions, monthlyFreeAvailable, totalAvailableCredits, isLoading, isLowCredits, isOutOfCredits } = usePractitionerCredits();

  if (isLoading) {
    return (
      <Card className={compact ? "h-24" : ""}>
        <CardContent className="p-4">
          <div className="animate-pulse">
            <div className="h-4 bg-muted rounded w-20 mb-2"></div>
            <div className="h-8 bg-muted rounded w-16"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!credits) {
    return (
      <Card className={compact ? "h-24" : ""}>
        <CardContent className="p-4">
          <div className="text-center text-muted-foreground">
            <Coins className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No credits data available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (compact) {
    return (
      <Card className="h-24">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground">Credits Available</p>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold">{totalAvailableCredits}</span>
                {isOutOfCredits && <AlertTriangle className="h-4 w-4 text-destructive" />}
                {isLowCredits && !isOutOfCredits && <TrendingDown className="h-4 w-4 text-warning" />}
              </div>
            </div>
            <Coins className="h-6 w-6 text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Main Balance Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Your Credit Balance</CardTitle>
          <Coins className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="flex items-baseline space-x-3 mb-4">
            <div className="text-2xl font-bold text-primary">
              {totalAvailableCredits}
            </div>
            <div className="text-sm text-muted-foreground">
              {credits?.credits_balance} purchased + {monthlyFreeAvailable} bonus
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Purchased Credits</p>
                    <p className="text-2xl font-bold">{credits?.credits_balance || 0}</p>
                  </div>
                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Bonus Credits</p>
                    <p className="text-2xl font-bold text-primary">{monthlyFreeAvailable}</p>
                  </div>
                  <Gift className="h-4 w-4 text-primary" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-muted-foreground">Total Available</p>
                    <p className="text-2xl font-bold text-primary">{totalAvailableCredits}</p>
                  </div>
                  <Coins className="h-4 w-4 text-primary" />
                </div>
              </CardContent>
            </Card>
          </div>

          {isOutOfCredits && (
            <Alert className="mt-4" variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                You're out of credits! Purchase more credits below to continue creating personalized sessions.
              </AlertDescription>
            </Alert>
          )}

          {isLowCredits && !isOutOfCredits && (
            <Alert className="mt-4">
              <TrendingDown className="h-4 w-4" />
              <AlertDescription>
                Running low on credits! You have {totalAvailableCredits} credits remaining. Consider purchasing more to avoid interruptions.
              </AlertDescription>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Usage Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>Usage Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Total Purchased</p>
              <p className="text-xl font-bold">{credits?.total_credits_purchased || 0}</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">Total Used</p>
              <p className="text-xl font-bold">{credits?.total_credits_used || 0}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {showTransactions && transactions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Recent Transactions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {transactions.slice(0, 5).map((transaction) => (
                <div key={transaction.id} className="flex items-center justify-between text-sm">
                  <div>
                    <span className="capitalize">{transaction.transaction_type}</span>
                    {transaction.notes && (
                      <span className="text-muted-foreground ml-2">- {transaction.notes}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={transaction.credits_amount > 0 ? 'text-green-600' : 'text-red-600'}>
                      {transaction.credits_amount > 0 ? '+' : ''}{transaction.credits_amount}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(transaction.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
